import { useRef, useEffect } from 'react';

import { useParams } from 'react-router-dom';

import { DocRow } from './doc-row';
import { Doc } from '@/api/types/backend-dto-types';
import { Empty, EmptyHeader, EmptyMedia, EmptyDescription } from '@/new/components/shadcn/empty';
import { Skeleton } from '@/new/components/shadcn/skeleton';

export function DocList({
  docs,
  isLoadingDocs,
  searchQuery,
  selectedDoc,
  setSelectedDoc,
}: {
  docs: (Doc & { hasKeyFacts: boolean; hasRelevantFacts: boolean; chunkMatchCount: number })[] | undefined;
  isLoadingDocs: boolean;
  searchQuery: string;
  selectedDoc: Doc | undefined;
  setSelectedDoc: (doc: Doc & { hasKeyFacts: boolean; hasRelevantFacts: boolean; chunkMatchCount: number }) => void;
}) {
  const { matterId } = useParams();
  if (!matterId) return;

  const docRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Scroll selected doc into view
  useEffect(() => {
    if (selectedDoc?.id) {
      const docElement = docRefs.current.get(selectedDoc.id);
      if (docElement) {
        docElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedDoc?.id]);

  return (
    <>
      {docs && docs.length > 0 ? (
        <>
          {docs.map((doc) => {
            return (
              <DocRow
                key={doc.id}
                ref={(el) => {
                  if (el) {
                    docRefs.current.set(doc.id, el);
                  } else {
                    docRefs.current.delete(doc.id);
                  }
                }}
                doc={doc}
                matterId={matterId}
                searchQuery={searchQuery}
                isSelected={doc.id === selectedDoc?.id}
                onSelect={() => {
                  setSelectedDoc(doc);
                }}
              />
            );
          })}
        </>
      ) : isLoadingDocs ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="rounded-lg border bg-white px-4 py-2 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <Skeleton className="h-4 w-3/4 bg-gray-100" />
                <Skeleton className="h-8 w-16" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-3 w-12 bg-gray-100" />
                <Skeleton className="h-3 w-1/2 bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center">
          <Empty className="max-w-md border border-gray-200 bg-gray-100">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <span className="iconify lucide--circle-question-mark h-5 w-5" />
              </EmptyMedia>

              <EmptyDescription>Could not find any documents.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      )}
    </>
  );
}
